import type { EcheanceItem } from '../types';

// Urgency thresholds for an échéance date. Mirrors the 14-day "soon" window
// used for project execution health (utils/executionHealth.ts) so overdue/
// due-soon read consistently across the app.
export type Urgency = 'overdue' | 'soon' | 'normal';

const SOON_DAYS = 14;

function todayStartMs(): number {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

/** Parses a 'YYYY-MM-DD' date string by components (no UTC-shift risk). */
export function parseYMD(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return NaN;
  return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
}

export function getUrgency(date: string): Urgency {
  return getUrgencyAt(date, todayStartMs());
}

/** Urgency of a date as seen from the start of another day (`refStartMs`) — the Gérance time slider's "today". */
export function getUrgencyAt(date: string, refStartMs: number): Urgency {
  const due = parseYMD(date);
  if (Number.isNaN(due)) return 'normal';
  if (due < refStartMs) return 'overdue';
  if (due <= refStartMs + SOON_DAYS * 86_400_000) return 'soon';
  return 'normal';
}

/** Whole days past an overdue date (1 = due yesterday), or 0 if it isn't overdue. */
export function daysOverdue(date: string): number {
  const due = parseYMD(date);
  if (Number.isNaN(due)) return 0;
  // Rounded so a DST switch between the two dates doesn't drop a day.
  return Math.max(0, Math.round((todayStartMs() - due) / 86_400_000));
}

export function isOverdue(item: EcheanceItem): boolean {
  return getUrgency(item.date) === 'overdue';
}

/** Sorts échéances soonest-first (the default order everywhere they're listed). */
export function sortByDate(items: EcheanceItem[]): EcheanceItem[] {
  return [...items].sort((a, b) => parseYMD(a.date) - parseYMD(b.date));
}

export interface EcheanceSummary {
  overdue: number;
  dueThisWeek: number;
  dueThisMonth: number;
}

/**
 * Counts for the Gérance summary strip. "This week"/"this month" are the
 * current calendar week (Mon–Sun) and calendar month — inclusive of each
 * other (a Tuesday deadline counts in both), not a mutually-exclusive split.
 * Overdue items are counted only as overdue.
 */
export function summarize(items: EcheanceItem[]): EcheanceSummary {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const dow = today.getDay(); // 0 Sun … 6 Sat
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const weekEnd = todayStart + (mondayOffset + 6) * 86_400_000; // Sunday, inclusive
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).getTime();

  let overdue = 0, dueThisWeek = 0, dueThisMonth = 0;
  for (const item of items) {
    const d = parseYMD(item.date);
    if (Number.isNaN(d)) continue;
    if (d < todayStart) { overdue++; continue; }
    if (d <= weekEnd) dueThisWeek++;
    if (d <= monthEnd) dueThisMonth++;
  }
  return { overdue, dueThisWeek, dueThisMonth };
}
